interface SeoSchemaProps {
  type: 'Organization' | 'WebSite' | 'WebPage' | 'BreadcrumbList' | 'Article' | 'FAQPage';
  data: Record<string, unknown>;
}

export function SeoSchema({ type, data }: SeoSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': type,
    ...data,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

interface BreadcrumbSchemaItem {
  name: string;
  item: string;
}

export function BreadcrumbSchema({ items }: { items: BreadcrumbSchemaItem[] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.item,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function getOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫阅读',
    url: 'https://hanmanyuedu.com',
    description: '免费无删减韩国漫画在线阅读平台，提供海量高质量韩漫资源，支持便捷的下拉式浏览体验。',
    sameAs: [
      'https://hanmanyuedu.com',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@hanman.com',
      availableLanguage: ['Chinese'],
    },
  };
}

export function getWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫阅读',
    url: 'https://hanmanyuedu.com',
    description: '访问此韩漫网站，免费阅读无删减韩国漫画大全，体验便捷的下拉式在线浏览，欣赏完整版好看作品。',
    inLanguage: 'zh-CN',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://hanmanyuedu.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };
}

export function getWebPageSchema(name: string, description: string, url: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫阅读',
      url: 'https://hanmanyuedu.com',
    },
  };
}

export function getArticleSchema(title: string, description: string, author: string, datePublished: string, image: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    author: {
      '@type': 'Person',
      name: author,
    },
    datePublished,
    image,
    inLanguage: 'zh-CN',
  };
}

export function getFAQSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫网站上的漫画都是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，韩漫阅读网站上的所有韩国漫画作品均完全免费阅读，无需付费订阅或购买。我们致力于为广大漫画爱好者提供无删减、完整版的高质量韩漫资源。',
        },
      },
      {
        '@type': 'Question',
        name: '什么是下拉式漫画阅读方式？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '下拉式阅读是一种专为手机和网页优化的漫画浏览方式，您只需向下滑动页面即可连续阅读漫画内容，无需翻页点击，带来流畅丝滑的阅读体验。这也是韩国网络漫画（Webtoon）最主流的呈现形式。',
        },
      },
      {
        '@type': 'Question',
        name: '网站上的漫画是否有删减？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '本站所有漫画均为无删减完整版，保持原作的完整内容和画风，让您体验到原汁原味的韩国漫画作品。我们尊重创作者的劳动成果，维护作品的完整性。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫网站支持哪些设备阅读？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫阅读网站采用响应式设计，完美支持电脑、平板和手机等多种设备。无论您使用什么设备，都能获得舒适流畅的下拉式阅读体验。推荐使用手机竖屏阅读，效果最佳。',
        },
      },
      {
        '@type': 'Question',
        name: '漫画更新频率是怎样的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '本站热门连载漫画通常保持每周更新一话的节奏，部分作品可能会根据作者进度有所调整。您可以收藏喜欢的作品，方便随时追踪最新章节的更新动态。',
        },
      },
    ],
  };
}